//
// Copyright (c) 2011-2022 by Curalate, Inc.
//

import { PayloadAction, createSlice } from '@reduxjs/toolkit';
import { PojoMap } from 'pojo-maps';

import { getItemsMetadata } from '@apis/bulkActionsApi';
import {
  getInstagramShopsAutocompleteSuggestions,
  getInstagramShopsItems,
  requestFeatureOnInstagramShops,
} from '@apis/instagram-shops/instagramShopsApi';
import {
  IInstagramShopsProduct,
  InstagramShopsItemFilter,
} from '@apis/instagram-shops/instagramShopsApiModel';
import {
  IAliasedDateRange,
  getAliasedDateRange,
} from '@components/date-range-picker/dateRangePickerModels';
import {
  generateInstagramSearchRequestBody,
  transformAutocompleteData,
} from '@instagram-account/instagramSharedUtils';
import { IInstagramSourceAndCountWithAutocomplete } from '@instagram-account/models/IInstagramAutocompleteResponses';
import {
  IInstagramShopsFeatureUgcState,
  INSTAGRAM_SHOPS_ITEM_REQUEST_LIMIT,
  NormalizedInstagramShopsItems,
  RightsRequestStateByCuid,
} from '@instagram-account/redux/instagram-shops/feature-ugc/instagramShopsFeatureUgcModel';
import { fetchInstagramShopsEligibility } from '@instagram-account/redux/instagram-shops/instagramShopsSlice';
import {
  requestFeatureOnInstagramShopsFailedToast,
  requestFeatureOnInstagramShopsSucceededToast,
} from '@instagram-account/redux/instagram-shops/instagramShopsToasts';
import { getInstagramShopsFeatureUgc } from '@instagram-account/redux/instagram-shops/selectors/getInstagramShopsFeatureUgc';
import { getInstagramShopsItemFilter } from '@instagram-account/redux/instagram-shops/selectors/getInstagramShopsItemFilter';
import { getOrderedInstagramShopsItems } from '@instagram-account/redux/instagram-shops/selectors/getOrderedInstagramShopItems';
import { getShouldRequestNextItems } from '@instagram-account/redux/instagram-shops/selectors/getShouldRequestNextItems';
import { getConnectedInstagramBusinessAccountId } from '@instagram-account/redux/selectors/getConnectedInstagramBusinessAccountId';
import { getDefaultInstagramAccountUsername } from '@instagram-account/redux/selectors/getDefaultInstagramAccountInfo';
import { toastNotificationsActions } from '@legacy-components/common/toast-notifications/toastNotificationsActions';
import Cuid from '@models/Cuid';
import { FilterMatchState } from '@models/FilterMatchState';
import { IMlsMediaItem } from '@models/IMlsMediaItem';
import { isDefinedWithNetworkItem } from '@models/INetworkItem';
import TimeZoneId from '@models/TimeZoneId';
import { UniversalSearchSortKey } from '@models/UniversalSearchSortKey';
import { SliceNames } from '@redux/SliceNames';
import { createThunk } from '@redux/createThunk';
import { isUndefined } from '@utils/filterPredicates';
import normalize, { Normalized } from '@utils/normalize';

import { crl8 } from 'app/curalate.globals';

const initialState: IInstagramShopsFeatureUgcState = {
  autocompleteResults: [],
  dateRange: getAliasedDateRange(
    'last90Days',
    crl8.session?.timeZone.id ?? TimeZoneId.Utc,
  ),
  errors: {},
  flags: {
    hasFetchedAllItems: false,
    isFetchingAutocomplete: false,
    isFetchingInitialItems: false,
    isFetchingNextItems: false,
    isInitialDataLoaded: false,
  },
  items: Normalized.empty(),
  itemFilter: InstagramShopsItemFilter.IsApprovedNotFeatured,
  rightsRequestStateByCuid: PojoMap.empty(),
  searchTerms: [],
  sort: UniversalSearchSortKey.PostedTimestamp,
  totalItemCount: 0,
};

const { actions, reducer } = createSlice({
  name: SliceNames.InstagramShopsFeatureUgc,
  initialState,
  reducers: {
    setIsInitialDataLoaded(state) {
      state.flags.isInitialDataLoaded = true;
    },
    setDateRange(state, action: PayloadAction<IAliasedDateRange>) {
      state.dateRange = action.payload;
    },
    addSearchTerm(
      state,
      action: PayloadAction<IInstagramSourceAndCountWithAutocomplete>,
    ) {
      state.searchTerms = [...state.searchTerms, action.payload];
    },
    setSearchTerms(
      state,
      action: PayloadAction<IInstagramSourceAndCountWithAutocomplete[]>,
    ) {
      state.searchTerms = [...action.payload];
    },
    setSort(state, action: PayloadAction<UniversalSearchSortKey>) {
      state.sort = action.payload;
    },
    setItemFilter(state, action: PayloadAction<InstagramShopsItemFilter>) {
      state.itemFilter = action.payload;
    },
    requestAutocomplete(state) {
      state.flags.isFetchingAutocomplete = true;
    },
    receiveAutocompleteSucceed(
      state,
      action: PayloadAction<IInstagramSourceAndCountWithAutocomplete[]>,
    ) {
      state.flags.isFetchingAutocomplete = false;
      state.autocompleteResults = [...action.payload];
    },
    receiveAutocompleteFailed(
      state,
      action: PayloadAction<string | undefined>,
    ) {
      state.flags.isFetchingAutocomplete = false;
      state.errors.fetchAutocompleteError = action.payload;
    },
    requestInitialInstagramShopsItems(state) {
      state.flags.isFetchingInitialItems = true;
      state.flags.hasFetchedAllItems = false;
      state.errors.fetchItemsError = undefined;
    },
    receiveInitialInstagramShopsItemsSucceeded(
      state,
      action: PayloadAction<{
        items: NormalizedInstagramShopsItems;
        totalItemCount: number;
        rightsRequestPermissions?: RightsRequestStateByCuid;
      }>,
    ) {
      state.flags.isFetchingInitialItems = false;
      state.items = Normalized.addItems(state.items, action.payload.items);
      state.totalItemCount = action.payload.totalItemCount;

      state.rightsRequestStateByCuid =
        action.payload.rightsRequestPermissions ?? PojoMap.empty();

      if (
        action.payload.items.orderedIds.length <
        INSTAGRAM_SHOPS_ITEM_REQUEST_LIMIT
      ) {
        state.flags.hasFetchedAllItems = true;
      }
    },
    receiveInitialInstagramShopsItemsFailed(
      state,
      action: PayloadAction<string>,
    ) {
      state.flags.isFetchingInitialItems = false;
      state.errors.fetchItemsError = action.payload;
    },
    requestNextInstagramShopsItems(state) {
      state.flags.isFetchingNextItems = true;
      state.errors.fetchItemsError = undefined;
    },
    receiveNextInstagramShopsItemsSucceeded(
      state,
      action: PayloadAction<{
        items: NormalizedInstagramShopsItems;
        rightsRequestPermissions?: RightsRequestStateByCuid;
      }>,
    ) {
      state.flags.isFetchingNextItems = false;
      state.items = Normalized.addItems(state.items, action.payload.items);

      state.rightsRequestStateByCuid = PojoMap.union(
        state.rightsRequestStateByCuid,
        action.payload.rightsRequestPermissions ?? PojoMap.empty(),
      );

      if (
        action.payload.items.orderedIds.length <
        INSTAGRAM_SHOPS_ITEM_REQUEST_LIMIT
      ) {
        state.flags.hasFetchedAllItems = true;
      }
    },
    receiveNextInstagramShopsItemsFailed(state, action: PayloadAction<string>) {
      state.flags.isFetchingNextItems = false;
      state.errors.fetchItemsError = action.payload;
    },
    receiveRightsRequestStatusFailed(state, action: PayloadAction<string>) {
      state.errors.fetchRightsRequestStatusError = action.payload;
    },
    updateInstagramShopsItems(state, action: PayloadAction<IMlsMediaItem[]>) {
      const updatedItemsById = PojoMap.fromIndexing(
        action.payload,
        (item) => item.cuid,
      );

      state.items.byId = PojoMap.union(state.items.byId, updatedItemsById);
    },
    resetContentState(state) {
      state.flags.hasFetchedAllItems = false;
      state.items = Normalized.empty();
    },
    resetState(state) {
      return initialState;
    },
  },
});

const getRightRequestStatus = createThunk(
  (items: IMlsMediaItem[]) => async (dispatch) => {
    const partialCuids = items
      .filter(isDefinedWithNetworkItem)
      .map((item) => Cuid.toPartial(item.cuid));

    try {
      const itemsMetadata = await getItemsMetadata(partialCuids);

      const rightsStatusByCuid = PojoMap.fromEntries(
        itemsMetadata.map(
          (item) => [item.rootCuid, item.rightsRequestStatus] as const,
        ),
      );

      return rightsStatusByCuid;
    } catch (_e) {
      dispatch(
        actions.receiveRightsRequestStatusFailed(
          'An error occurred while fetching rights request status. Please try again or contact Bazaarvoice Support.',
        ),
      );
    }
  },
);

const fetchInstagramShopsItems = createThunk(
  (offset: number = 0) =>
    async (dispatch, getState) => {
      const businessUserId = getConnectedInstagramBusinessAccountId(getState());
      const mentionedUsername = getDefaultInstagramAccountUsername(getState());

      if (isUndefined(businessUserId) || isUndefined(mentionedUsername)) {
        return;
      }

      const { dateRange, searchTerms, sort } =
        getInstagramShopsFeatureUgc(getState());

      const searchedBody = generateInstagramSearchRequestBody(searchTerms);

      const itemFilter = getInstagramShopsItemFilter(getState());

      const { items, totalCount } = await getInstagramShopsItems(
        {
          businessUserId,
          mentionedUsername,
          itemFilter,
          matchState: FilterMatchState.All,
          startTime: dateRange.startDate,
          endTime: dateRange.endDate,
          offset,
          limit: INSTAGRAM_SHOPS_ITEM_REQUEST_LIMIT,
          sortKey: sort,
        },
        searchedBody,
      );

      const rightsRequestPermissions = await dispatch(
        getRightRequestStatus(items),
      );

      return {
        items: normalize(items, (item) => item.cuid),
        totalItemCount: totalCount,
        rightsRequestPermissions,
      };
    },
);

export const loadInitialInstagramShopsData = createThunk(
  () => async (dispatch) => {
    await Promise.all([
      dispatch(fetchInstagramShopsEligibility()),
      dispatch(getInitialInstagramShopsItems()),
    ]);
    dispatch(actions.setIsInitialDataLoaded());
  },
);

/**
 * Gets first set of Instagram Shops items. Should be used on page load or after selecting a new filter.
 */
export const getInitialInstagramShopsItems = createThunk(
  () => async (dispatch) => {
    dispatch(actions.requestInitialInstagramShopsItems());
    dispatch(actions.resetContentState());

    try {
      const itemsResponse = await dispatch(fetchInstagramShopsItems());

      if (isUndefined(itemsResponse)) {
        return;
      }

      const { items, rightsRequestPermissions, totalItemCount } = itemsResponse;

      dispatch(
        actions.receiveInitialInstagramShopsItemsSucceeded({
          items,
          totalItemCount,
          rightsRequestPermissions,
        }),
      );
    } catch (_e) {
      dispatch(
        actions.receiveInitialInstagramShopsItemsFailed(
          'An error occurred while fetching Instagram Shops items. Please try again or contact Bazaarvoice Support.',
        ),
      );
    }
  },
);

/**
 * Gets additional Instagram Shops items. Should be used when user scrolls to the bottom of the item grid.
 */
export const getNextInstagramShopsItems = createThunk(
  () => async (dispatch, getState) => {
    const currentItems = getOrderedInstagramShopsItems(getState());
    const shouldRequestNextItems = getShouldRequestNextItems(getState());

    if (!shouldRequestNextItems) {
      // Don't initiate another request if there is one already in flight, or we already have all items
      return;
    }

    dispatch(actions.requestNextInstagramShopsItems());

    try {
      const itemsResponse = await dispatch(
        fetchInstagramShopsItems(currentItems.length),
      );

      if (isUndefined(itemsResponse)) {
        return;
      }

      const { items, rightsRequestPermissions } = itemsResponse;

      dispatch(
        actions.receiveNextInstagramShopsItemsSucceeded({
          items,
          rightsRequestPermissions,
        }),
      );
    } catch (_e) {
      dispatch(
        actions.receiveNextInstagramShopsItemsFailed(
          'An error occurred while fetching more Instagram Shops items. Please try again or contact Bazaarvoice Support.',
        ),
      );
    }
  },
);

export const setTermFilterAndRefreshContent = createThunk(
  (filterOption: IInstagramSourceAndCountWithAutocomplete) =>
    async (dispatch, getState) => {
      const { searchTerms } = getInstagramShopsFeatureUgc(getState());

      const hasSearchTerm = searchTerms.some(
        (term) => term.source === filterOption.source,
      );

      if (hasSearchTerm) {
        return;
      }

      dispatch(actions.addSearchTerm(filterOption));
      dispatch(getInitialInstagramShopsItems());
    },
);

export const setSortAndRefreshContent = createThunk(
  (sort: UniversalSearchSortKey) => (dispatch, getState) => {
    dispatch(actions.setSort(sort));
    dispatch(getInitialInstagramShopsItems());
  },
);

export const setDateRangeAndRefreshContent = createThunk(
  (dateRange: IAliasedDateRange) => (dispatch, getState) => {
    dispatch(actions.setDateRange(dateRange));
    dispatch(getInitialInstagramShopsItems());
  },
);

export const removeFilterByIndexAndRefreshContent = createThunk(
  (index: number) => (dispatch, getState) => {
    const { searchTerms } = getInstagramShopsFeatureUgc(getState());

    const updatedSearchTerms = [
      ...searchTerms.slice(0, index),
      ...searchTerms.slice(index + 1),
    ];

    dispatch(actions.setSearchTerms(updatedSearchTerms));
    dispatch(getInitialInstagramShopsItems());
  },
);

export const removeAllFiltersAndRefreshContent = createThunk(
  () => (dispatch, getState) => {
    dispatch(actions.setSearchTerms([]));
    dispatch(getInitialInstagramShopsItems());
  },
);

export const fetchAutoComplete = createThunk(
  (searchTerm: string = '') =>
    async (dispatch, getState) => {
      const { dateRange, searchTerms } =
        getInstagramShopsFeatureUgc(getState());
      const businessUserId = getConnectedInstagramBusinessAccountId(getState());
      const mentionedUsername = getDefaultInstagramAccountUsername(getState());

      if (isUndefined(businessUserId) || isUndefined(mentionedUsername)) {
        return;
      }

      dispatch(actions.requestAutocomplete());

      try {
        const searchedBody = generateInstagramSearchRequestBody(searchTerms);

        const itemFilter = getInstagramShopsItemFilter(getState());

        const autocompleteData = await getInstagramShopsAutocompleteSuggestions(
          {
            businessUserId,
            mentionedUsername,
            itemFilter,
            searchTerm,
            matchState: FilterMatchState.All,
            startTime: dateRange.startDate,
            endTime: dateRange.endDate,
          },
          searchedBody,
        );

        const autocompleteResults = transformAutocompleteData(
          autocompleteData.data,
        );

        dispatch(actions.receiveAutocompleteSucceed(autocompleteResults));
      } catch (_e) {
        dispatch(
          actions.receiveAutocompleteFailed(
            'An error occurred while fetching autocomplete suggestions. Please try again or contact Bazaarvoice Support.',
          ),
        );
      }
    },
);

export const requestToFeatureOnInstagramShops = createThunk(
  (cuid: Cuid, products: IInstagramShopsProduct[]) =>
    async (dispatch, getState): Promise<void> => {
      const businessUserId = getConnectedInstagramBusinessAccountId(getState());

      if (isUndefined(businessUserId)) {
        return;
      }

      try {
        const productIds = products.map((p) => p.productId);

        await requestFeatureOnInstagramShops({
          businessUserId,
          cuid,
          productIds,
        });
        dispatch(
          toastNotificationsActions.addToastNotification(
            requestFeatureOnInstagramShopsSucceededToast,
          ),
        );
        void (await dispatch(getInitialInstagramShopsItems()));
      } catch (e) {
        dispatch(
          toastNotificationsActions.addToastNotification(
            requestFeatureOnInstagramShopsFailedToast,
          ),
        );
      }
    },
);

export const setItemFilterAndFetchItems = createThunk(
  (filter: InstagramShopsItemFilter) => (dispatch) => {
    dispatch(actions.setItemFilter(filter));
    void dispatch(getInitialInstagramShopsItems());
  },
);

export default reducer;
